// This file is effectively replacing the selectors folder
// It will allow us to use a combination of react-query and redux
import { DimensionValueType, GetApiDimensionsQueryParamsUpdated, GetApiDimensionsResponse } from '@sit/client-shared';
import { useGetCurrentCompany } from '@web/api/company/use-get-company';
import { useGetDimensions } from '@web/api/dimensions/use-get-dimensions';
import { useShowIntacctId } from '@web/api/show-intacct-id';
import { useGetTimesheets } from '@web/api/timesheets/use-get-timesheets';
import { adminManagerRegEx, internalTenantTypeRegEx, notApprovedStatusRegEx } from '@web/constants/regularExpressions';
import { includeIntacctId, sortObjectsByNameAsc } from '@web/helpers/utils';
import { ClientsStoreItem } from '@web/types/redux/stores/clients.types';
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import { useMemo } from 'react';
import {
  getClusterAssignModal,
  getClustersData,
  getTimesheetEntriesData,
  getTimesheetId,
  getUserData,
  selectTimesheetData,
  selectUserData,
} from '../selectors';
import {
  filterAssignedClusters,
  filterUnassignedClusters,
  groupAndSortClusters,
  includeIntacctIdInClusterDimensionNames,
  includeIntacctIdInEntryDimensionNames,
} from '../selectors/helpers';
import { useAppSelector } from '../store';

// company
export function useCompanyEntities() {
  const { data: company } = useGetCurrentCompany();
  return company?.entities;
}
export function useDisableMFASwitch() {
  const { data: company } = useGetCurrentCompany();
  const userData = useAppSelector(getUserData);
  if (company?.tenantType === 'debug') return true;
  const intacctCompanyId = userData?.company?.intacctCompanyId;
  return !intacctCompanyId;
}

export function useNeedsToSetUpMfa() {
  const userData = useAppSelector(getUserData);
  const { data: company } = useGetCurrentCompany();
  return !!company?.mfaRequired && !userData?.twoFactorEnabled;
}

export function useIsInternalTenant() {
  const { data: company } = useGetCurrentCompany();
  return internalTenantTypeRegEx.test(company?.tenantType || 'debug');
}

// timesheets

export function useTimesheetEntriesData() {
  const showIntacctId = useShowIntacctId();
  const entriesData = useAppSelector(getTimesheetEntriesData);
  return useMemo(() => {
    if (!showIntacctId) return entriesData;
    const entriesCopy = cloneDeep(entriesData);
    for (const entryId of Object.keys(entriesCopy)) {
      entriesCopy[entryId] = includeIntacctIdInEntryDimensionNames(entriesCopy[entryId]);
    }
    return entriesCopy;
  }, [entriesData, showIntacctId]);
}

function useApprovedTimesheets() {
  return useGetTimesheets({
    select: (data) => data.filter((t) => t.status === 'APPROVED').sort((a, b) => b.endDate.localeCompare(a.endDate)),
  });
}

export function useActiveTimesheets() {
  const { data: timesheets } = useGetTimesheets();
  const { data: approvedTimesheets } = useApprovedTimesheets();
  const lastApprovedTimesheet = approvedTimesheets?.[0];
  return useMemo(() => {
    if (!timesheets) return undefined;
    return timesheets.filter((timesheet) => {
      if (notApprovedStatusRegEx.test(timesheet.status)) {
        return true;
      }
      if (timesheet.status === 'APPROVED') {
        return lastApprovedTimesheet?.id === timesheet.id;
      }
      return true;
    });
  }, [lastApprovedTimesheet, timesheets]);
}

export function useEntryById(entryId?: string) {
  const showIntacctId = useShowIntacctId();
  const entriesData = useAppSelector(getTimesheetEntriesData);
  if (!entryId || !entriesData[entryId]) return null;
  if (!showIntacctId) return entriesData[entryId];
  return includeIntacctIdInEntryDimensionNames(entriesData[entryId]);
}

export function useTimesheetRowsData() {
  return useAppSelector((state) => state.timesheetRows?.data);
}

export function useRowFirstEntry(row: any | undefined) {
  const showIntacctId = useShowIntacctId();
  const entriesData = useAppSelector(getTimesheetEntriesData);
  const firstRowEntryId = row?.entries?.[0]?.id;
  if (!firstRowEntryId || !entriesData[firstRowEntryId]) return null;
  if (!showIntacctId) return entriesData[firstRowEntryId];
  return includeIntacctIdInEntryDimensionNames(entriesData[firstRowEntryId]);
}

// Timesheet
export function useTimesheetData() {
  return useAppSelector(selectTimesheetData);
}
export function useTimesheetSaving() {
  return useAppSelector((state) => state.timesheet.saving);
}
export function useTimesheetSyncStatus() {
  return useAppSelector((state) => state.timesheet.synced) ?? false;
}
export function useTimesheetStartDate() {
  const { startDate } = useTimesheetData();
  return startDate ? startDate.substring(0, 10) : '';
}
export function useTimesheetEndDate() {
  const { endDate } = useTimesheetData();
  return endDate ? endDate.substring(0, 10) : '';
}
export function useTimesheetDescription() {
  return useTimesheetData()?.description;
}
export function useTimesheetStatus() {
  return useTimesheetData().status || 'NO_STATUS';
}
export function useSubmitButtonDisabled() {
  const timesheetStatus = useTimesheetStatus();
  const entriesData = useTimesheetEntriesData();
  return /^(INVOICED)$/.test(timesheetStatus) || Object.keys(entriesData).length === 0;
}

// Dimensions

export function useClients() {
  const clientsData = useAppSelector((state) => state.clients.data) as ClientsStoreItem[] | undefined;
  const showIntacctId = useShowIntacctId();
  return useMemo(() => {
    if (!clientsData) return [];
    return (showIntacctId ? clientsData.map((d) => includeIntacctId(d)) : [...clientsData]).sort(sortObjectsByNameAsc);
  }, [clientsData, showIntacctId]);
}

/**
 *     Projects
 */
export function useProjects() {
  const showIntacctId = useShowIntacctId();
  const projectsData = useAppSelector((state) => state.projects.data);
  return useMemo(() => {
    if (!projectsData) return [];
    return (showIntacctId ? projectsData.map((d) => includeIntacctId(d)) : [...projectsData]).sort(sortObjectsByNameAsc);
  }, [projectsData, showIntacctId]);
}

export function useProjectsPerClientId(clientId: string | null | undefined) {
  const perClientId = useAppSelector((state) => state.projects.perClientId);
  const showIntacctId = useShowIntacctId();
  const showCustomer = useShowCustomer();
  return useMemo(() => {
    const data = (() => {
      if (!showCustomer) return perClientId.all?.data || [];
      return (clientId && perClientId?.[clientId]?.data) || [];
    })();
    return (showIntacctId ? data.map((d) => includeIntacctId(d)) : [...data]).sort(sortObjectsByNameAsc);
  }, [clientId, perClientId, showCustomer, showIntacctId]);
}
export function useProjectsPerClientIdOfClusterAssignModal() {
  const perClientId = useAppSelector((state) => state.projects.perClientId);
  const showCustomer = useShowCustomer();
  const showIntacctId = useShowIntacctId();
  const clusterAssignModal = useAppSelector(getClusterAssignModal);
  return useMemo(() => {
    const data = (() => {
      if (!showCustomer) return perClientId.all?.data || [];
      if (!perClientId || !clusterAssignModal?.selectedValues) return [];
      const { clientId } = clusterAssignModal.selectedValues;
      if (!clientId) return [];
      return perClientId[clientId]?.data ?? [];
    })();
    return (showIntacctId ? data.map((d) => includeIntacctId(d)) : [...data]).sort(sortObjectsByNameAsc);
  }, [clusterAssignModal.selectedValues, perClientId, showCustomer, showIntacctId]);
}

export function useTasks() {
  const showIntacctId = useShowIntacctId();
  const tasksData = useAppSelector((state) => state.tasks.data);
  return useMemo(() => {
    if (!tasksData) return [];
    return (showIntacctId ? tasksData.map((d) => includeIntacctId(d)) : [...tasksData]).sort(sortObjectsByNameAsc);
  }, [showIntacctId, tasksData]);
}

export function useTasksPerProjectId(projectId: string | null | undefined) {
  const perProjectId = useAppSelector((state) => state.tasks.perProjectId);
  const showProject = useShowProject();
  const showIntacctId = useShowIntacctId();
  return useMemo(() => {
    const data = (() => {
      if (!showProject) return perProjectId.all?.data || [];
      if (!perProjectId || !projectId) return [];
      return perProjectId[projectId]?.data || [];
    })();
    return (showIntacctId ? data.map((d) => includeIntacctId(d)) : [...data]).sort(sortObjectsByNameAsc);
  }, [perProjectId, projectId, showIntacctId, showProject]);
}

export function useTasksPerProjectIdOfClusterAssignModal() {
  const showProject = useShowProject();
  const showIntacctId = useShowIntacctId();
  const clusterAssignModal = useAppSelector(getClusterAssignModal);
  const perProjectId = useAppSelector((state) => state.tasks.perProjectId);
  return useMemo(() => {
    const data = (() => {
      if (!showProject) return perProjectId.all?.data || [];
      if (!clusterAssignModal?.selectedValues || !perProjectId) return [];
      const projectId = clusterAssignModal.selectedValues?.projectId;
      return projectId ? perProjectId[projectId]?.data || [] : [];
    })();
    return (showIntacctId ? data.map((d) => includeIntacctId(d)) : [...data]).sort(sortObjectsByNameAsc);
  }, [clusterAssignModal.selectedValues, showIntacctId, showProject, perProjectId]);
}

export function useDimensionsData(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const { data: dimensionsData = [] } = useGetDimensions(queryParams);
  const showIntacctId = useShowIntacctId();
  return useMemo(() => {
    if (!showIntacctId) return dimensionsData;
    return dimensionsData.map((d) => ({
      ...d,
      values: d.values?.map(includeIntacctId),
    }));
  }, [dimensionsData, showIntacctId]);
}

const findDepartmentDimension = (dimensionsData: GetApiDimensionsResponse) =>
  dimensionsData?.find((d) => d.integrationMeta?.objectName === 'DEPARTMENT');
const findLocationDimension = (dimensionsData: GetApiDimensionsResponse) =>
  dimensionsData?.find((d) => d.integrationMeta?.objectName === 'LOCATION');
const findCostTypeDimension = (dimensionsData: GetApiDimensionsResponse) =>
  dimensionsData?.find((d) => d.integrationMeta?.objectName === 'COSTTYPE');
const findEmployeePositionDimension = (dimensionsData: GetApiDimensionsResponse) =>
  dimensionsData?.find((d) => d.integrationMeta?.objectName === 'EMPLOYEEPOSITION');
const findLaborClassDimension = (dimensionsData: GetApiDimensionsResponse) =>
  dimensionsData?.find((d) => d.integrationMeta?.objectName === 'LABORCLASS');
const findLaborShiftDimension = (dimensionsData: GetApiDimensionsResponse) =>
  dimensionsData?.find((d) => d.integrationMeta?.objectName === 'LABORSHIFT');
const findLaborUnionDimension = (dimensionsData: GetApiDimensionsResponse) =>
  dimensionsData?.find((d) => d.integrationMeta?.objectName === 'LABORUNION');

export function useDepartmentDimension(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findDepartmentDimension(dimensionsData);
}

export function useLocationDimension(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findLocationDimension(dimensionsData);
}

export function useCostTypeDimension(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findCostTypeDimension(dimensionsData);
}

export function useEmployeePositionDimension(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findEmployeePositionDimension(dimensionsData);
}

export function useLaborClassDimension(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findLaborClassDimension(dimensionsData);
}

export function useLaborShiftDimension(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findLaborShiftDimension(dimensionsData);
}

export function useLaborUnionDimension(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findLaborUnionDimension(dimensionsData);
}

const emptyArr: DimensionValueType[] = [];

export function useDepartmentOptions(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findDepartmentDimension(dimensionsData)?.values || emptyArr;
}
export function useLocationOptions(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findLocationDimension(dimensionsData)?.values || emptyArr;
}
export function useCostTypeOptions(queryParams: GetApiDimensionsQueryParamsUpdated) {
  const dimensionsData = useDimensionsData(queryParams);
  return findCostTypeDimension(dimensionsData)?.values || emptyArr;
}

export function useClusterEntry(cluster: { timesheet_entry_id?: string | null | undefined } | undefined) {
  const showIntacctId = useShowIntacctId();
  const entriesData = useAppSelector(getTimesheetEntriesData);
  const clusterEntryId = cluster?.timesheet_entry_id;
  if (!clusterEntryId || !entriesData[clusterEntryId]) return null;
  if (!showIntacctId) return entriesData[clusterEntryId];
  return {
    ...entriesData[clusterEntryId],
    client: includeIntacctId(entriesData[clusterEntryId]),
    project: includeIntacctId(entriesData[clusterEntryId]),
    task: includeIntacctId(entriesData[clusterEntryId]),
    item: includeIntacctId(entriesData[clusterEntryId]),
  };
}

export function useAssignedClusters() {
  const clustersData = useAppSelector(getClustersData);
  const entriesData = useTimesheetEntriesData();
  const timesheetId = useAppSelector(getTimesheetId);
  const showIntacctId = useShowIntacctId();
  return useMemo(() => {
    if (!showIntacctId) return groupAndSortClusters(filterAssignedClusters(clustersData, entriesData, timesheetId));
    const updatedClusterData = compact(clustersData.map((c) => includeIntacctIdInClusterDimensionNames(c)));
    return groupAndSortClusters(filterAssignedClusters(updatedClusterData, entriesData, timesheetId));
  }, [clustersData, entriesData, showIntacctId, timesheetId]);
}

export function useUnassignedClusters() {
  const clustersData = useAppSelector(getClustersData);
  const showIntacctId = useShowIntacctId();
  return useMemo(() => {
    if (!showIntacctId) return groupAndSortClusters(filterUnassignedClusters(clustersData));
    const updatedClusterData = clustersData.map((c) => includeIntacctIdInClusterDimensionNames(c));
    return groupAndSortClusters(filterUnassignedClusters(updatedClusterData));
  }, [clustersData, showIntacctId]);
}

export function useShowCustomer() {
  const timesheetData = useTimesheetData();
  const { data: company } = useGetCurrentCompany();
  return timesheetData.showCustom ?? !!company?.showCustom;
}

export function useShowProject() {
  const timesheetData = useTimesheetData();
  const { data: company } = useGetCurrentCompany();
  return timesheetData.showProject ?? !!company?.showProject;
}

export function useShowTask() {
  const timesheetData = useTimesheetData();
  const { data: company } = useGetCurrentCompany();
  return timesheetData.showTask ?? !!company?.showTask;
}

export function useShowCustomerProjectTask() {
  return {
    showCustomer: useShowCustomer(),
    showProject: useShowProject(),
    showTask: useShowTask(),
  };
}

export function useShowDepartmentLocation() {
  const timesheetData = useTimesheetData();
  const { data: company } = useGetCurrentCompany();
  return timesheetData.showDepartmentLocation ?? !!company?.showDepartmentLocation;
}

export function useShowDescriptionFirst() {
  const timesheetData = useTimesheetData();
  const { data: company } = useGetCurrentCompany();
  return timesheetData.showDescriptionFirst ?? !!company?.showDescriptionFirst;
}

export function useTimesheetDimensions() {
  const t = useTimesheetData();
  const { data: company } = useGetCurrentCompany();
  return {
    showBillable: t.showBillable ?? !!company?.showBillable,
    showCustom: t.showCustom ?? !!company?.showCustom,
    showDepartmentLocation: t.showDepartmentLocation ?? !!company?.showDepartmentLocation,
    showItem: t.showItem ?? !!company?.showItem,
    showProject: t.showProject ?? !!company?.showProject,
    showTask: t.showTask ?? !!company?.showTask,
    showTimeType: t.showTimeType ?? !!company?.showTimeType,
    showCostType: t.showCostType ?? !!company?.showCostType,
    showEmployeePosition: t.showEmployeePosition ?? !!company?.showEmployeePosition,
    showLaborClass: t.showLaborClass ?? !!company?.showLaborClass,
    showLaborShift: t.showLaborShift ?? !!company?.showLaborShift,
    showLaborUnion: t.showLaborUnion ?? !!company?.showLaborUnion,
  };
}

export function useClientDisplayName() {
  return useGetCurrentCompany().data?.clientDisplayName;
}

export function useProjectDisplayName() {
  return useGetCurrentCompany().data?.projectDisplayName;
}

export function useTaskDisplayName() {
  return useGetCurrentCompany().data?.taskDisplayName;
}

export function useItemDisplayName() {
  return useGetCurrentCompany().data?.itemDisplayName;
}

export function useDepartmentDisplayName() {
  return useGetCurrentCompany().data?.departmentDisplayName;
}

export function useLocationDisplayName() {
  return useGetCurrentCompany().data?.locationDisplayName;
}

export function useDimensionDisplayNames() {
  const {
    clientDisplayName = null,
    projectDisplayName = null,
    taskDisplayName = null,
    itemDisplayName = null,
    departmentDisplayName = null,
    locationDisplayName = null,
    costTypeDisplayName = null,
    employeePositionDisplayName = null,
    laborClassDisplayName = null,
    laborShiftDisplayName = null,
    laborUnionDisplayName = null,
  } = useGetCurrentCompany().data || {};
  return {
    clientDisplayName,
    projectDisplayName,
    taskDisplayName,
    itemDisplayName,
    departmentDisplayName,
    locationDisplayName,
    costTypeDisplayName,
    employeePositionDisplayName,
    laborClassDisplayName,
    laborShiftDisplayName,
    laborUnionDisplayName,
  };
}

export function useClusters() {
  return useAppSelector((state) => state.clusters || {});
}

export function useHasAccessToUserManagement() {
  const userData = useAppSelector(getUserData);
  return adminManagerRegEx.test(userData?.role || 'NO_USER_ROLE');
}

export function useEntryOfClusterAssignModal() {
  const clusterAssignModal = useAppSelector(getClusterAssignModal);
  const clustersData = useAppSelector(getClustersData);
  const entriesData = useAppSelector(getTimesheetEntriesData);
  const entryId = (clustersData?.find((c) => c.id === clusterAssignModal.cluster.id) || clusterAssignModal.cluster)?.timesheet_entry_id;
  if (!entryId) return null;
  return entriesData[entryId] || null;
}

// Companies
export function useCompanies() {
  return useAppSelector(getUserData).companies;
}

export function useAttachmentRights() {
  const company = useGetCurrentCompany().data;
  const user = useAppSelector(getUserData);
  return {
    canEditAttachments: !!company?.canEditAttachments && !!user?.canEditAttachments,
    canDownloadAttachments: !!company?.canDownloadAttachments && !!user?.canDownloadAttachments,
    canDeleteAttachments: !!company?.canDeleteAttachments && !!user?.canDeleteAttachments,
    canListAttachments: !!company?.canListAttachments && !!user?.canListAttachments,
  };
}

// users
export function useCanViewMyTimesheets() {
  return useAppSelector(selectUserData).canViewMyTimesheets ?? true;
}
export function useCanListMyTimesheets() {
  return useAppSelector(selectUserData).canListMyTimesheets ?? true;
}
export function useCanViewStaffTimesheets() {
  return useAppSelector(selectUserData).hasStaffTimesheets ?? false;
}

// approvals

export function useApprovalsDoneAllLoading() {
  return useAppSelector((state) => (state.approvals.done?.all?.loading || false) as boolean);
}

// auth
export function useIsAuthenticated() {
  return useAppSelector((state) => state.user.token != null);
}
